import React from "react";
import {
  ShieldCheck,
  Database,
  UserCheck,
  Cookie,
  LockKeyhole,
  Scale,
  ExternalLink,
  RefreshCw,
  Mail,
  Phone,
  Globe,
} from "lucide-react";

const PrivacyPolicy = () => {
  const sections = [
    {
      id: "information-we-collect",
      number: "01",
      title: "Information We Collect",
      icon: Database,
      content: (
        <ul className="space-y-3 text-slate-600">
          <li>
            <strong className="text-slate-900">
              Personal Information:
            </strong>{" "}
            Name, email address, phone number, account details, and other
            information you voluntarily provide.
          </li>

          <li>
            <strong className="text-slate-900">
              Usage Data:
            </strong>{" "}
            Information about how you interact with our website, products, and
            services.
          </li>

          <li>
            <strong className="text-slate-900">
              Device Information:
            </strong>{" "}
            IP address, browser type, operating system, device type, and related
            technical information.
          </li>
        </ul>
      ),
    },
    {
      id: "how-we-use-information",
      number: "02",
      title: "How We Use Your Information",
      icon: UserCheck,
      content: (
        <ul className="space-y-3 text-slate-600">
          <li>Provide, operate, and manage our products and services.</li>
          <li>Communicate with you regarding your account or requests.</li>
          <li>Improve our platforms, products, and user experience.</li>
          <li>Provide customer support and respond to inquiries.</li>
          <li>Protect our systems against misuse, fraud, and security threats.</li>
          <li>
            Send service-related updates or promotional communication where
            permitted.
          </li>
        </ul>
      ),
    },
    {
      id: "sharing-information",
      number: "03",
      title: "Sharing Your Information",
      icon: ShieldCheck,
      content: (
        <p className="text-slate-600 leading-relaxed">
          Nexindi Tech does not sell your personal information. We may share
          information with trusted service providers that help us operate our
          platforms, process payments, host services, communicate with users,
          or provide technical infrastructure. We may also disclose information
          when required by law or where necessary to protect our legal rights,
          users, or systems.
        </p>
      ),
    },
    {
      id: "cookies",
      number: "04",
      title: "Cookies and Tracking Technologies",
      icon: Cookie,
      content: (
        <p className="text-slate-600 leading-relaxed">
          We may use cookies and similar technologies to remember preferences,
          understand website usage, improve performance, and support certain
          functionality. You can control or disable cookies through your
          browser settings, although some features may not work as intended.
        </p>
      ),
    },
    {
      id: "security",
      number: "05",
      title: "Data Security",
      icon: LockKeyhole,
      content: (
        <p className="text-slate-600 leading-relaxed">
          We implement reasonable technical and organizational safeguards
          designed to protect personal information against unauthorized
          access, loss, misuse, alteration, or disclosure. However, no online
          system or method of electronic storage can guarantee absolute
          security.
        </p>
      ),
    },
    {
      id: "rights",
      number: "06",
      title: "Your Privacy Rights",
      icon: Scale,
      content: (
        <div className="text-slate-600">
          <p className="mb-4">
            Depending on applicable law, you may have the right to:
          </p>

          <ul className="space-y-3">
            <li>Request access to personal information we hold about you.</li>
            <li>Request correction of inaccurate or incomplete information.</li>
            <li>Request deletion of personal information where applicable.</li>
            <li>Object to or restrict certain processing activities.</li>
            <li>Withdraw consent where processing is based on consent.</li>
          </ul>
        </div>
      ),
    },
    {
      id: "third-party-links",
      number: "07",
      title: "Third-Party Links and Services",
      icon: ExternalLink,
      content: (
        <p className="text-slate-600 leading-relaxed">
          Our websites and products may contain links to external services or
          websites operated by third parties. Nexindi Tech is not responsible
          for the privacy practices, content, or security of those third-party
          services. We encourage you to review their privacy policies before
          providing personal information.
        </p>
      ),
    },
    {
      id: "policy-updates",
      number: "08",
      title: "Changes to This Privacy Policy",
      icon: RefreshCw,
      content: (
        <p className="text-slate-600 leading-relaxed">
          We may update this Privacy Policy from time to time to reflect
          changes in our services, technology, business practices, or legal
          requirements. Any updated version will be posted on this page with a
          revised effective date.
        </p>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-24 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-7">
            <ShieldCheck size={15} />
            Legal & Privacy
          </div>

          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            Privacy Policy
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            This policy explains how Nexindi Tech collects, uses, protects,
            and manages information when you use our websites, digital
            products, and services.
          </p>

          <div className="mt-8 inline-flex items-center gap-2 text-sm text-slate-400">
            <span>Effective Date:</span>
            <strong className="text-white">
              May 26, 2025
            </strong>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="px-6 py-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[260px_1fr] gap-10 xl:gap-16">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-400 font-bold mb-5">
                On this page
              </p>

              <nav className="space-y-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="block px-4 py-3 rounded-xl text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition"
                  >
                    {section.number}. {section.title}
                  </a>
                ))}

                <a
                  href="#contact"
                  className="block px-4 py-3 rounded-xl text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition"
                >
                  09. Contact Us
                </a>
              </nav>
            </div>
          </aside>

          {/* Main Legal Content */}
          <div className="max-w-4xl">
            {/* Intro */}
            <div className="bg-white border border-slate-200 rounded-3xl p-7 md:p-9 shadow-xl shadow-slate-900/5 mb-8">
              <p className="text-lg text-slate-600 leading-relaxed">
                At{" "}
                <strong className="text-slate-900">
                  Nexindi Tech Company
                </strong>
                , we value your privacy and are committed to handling personal
                information responsibly. This Privacy Policy applies to our
                website, products, platforms, and services unless a specific
                service provides a separate privacy notice.
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-6">
              {sections.map((section) => {
                const Icon = section.icon;

                return (
                  <section
                    key={section.id}
                    id={section.id}
                    className="scroll-mt-28 bg-white border border-slate-200 rounded-3xl p-7 md:p-9"
                  >
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                        <Icon size={21} />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-xs font-bold text-blue-600">
                            {section.number}
                          </span>

                          <h2 className="text-xl md:text-2xl font-bold text-slate-900">
                            {section.title}
                          </h2>
                        </div>

                        {section.content}
                      </div>
                    </div>
                  </section>
                );
              })}

              {/* Contact */}
              <section
                id="contact"
                className="scroll-mt-28 relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-slate-950 p-7 md:p-9 text-white"
              >
                <div className="absolute -top-24 -right-20 w-72 h-72 bg-cyan-300/10 rounded-full blur-[90px]" />

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-xs font-bold text-blue-200">
                      09
                    </span>

                    <h2 className="text-2xl font-bold">
                      Contact Us
                    </h2>
                  </div>

                  <p className="text-blue-100 leading-relaxed max-w-2xl mb-7">
                    If you have questions, concerns, or requests regarding this
                    Privacy Policy or how your personal information is handled,
                    you can contact Nexindi Tech through the details below.
                  </p>

                  <div className="grid md:grid-cols-3 gap-4">
                    <a
                      href="mailto:nexinditechcompany@gmail.com"
                      className="flex items-center gap-3 rounded-2xl bg-white/10 border border-white/10 p-4 hover:bg-white/15 transition"
                    >
                      <Mail size={18} className="text-blue-200" />

                      <div className="min-w-0">
                        <p className="text-xs text-blue-200 mb-1">
                          Email
                        </p>

                        <p className="text-sm font-semibold truncate">
                          nexinditechcompany@gmail.com
                        </p>
                      </div>
                    </a>

                    <a
                      href="tel:+254797655727"
                      className="flex items-center gap-3 rounded-2xl bg-white/10 border border-white/10 p-4 hover:bg-white/15 transition"
                    >
                      <Phone size={18} className="text-blue-200" />

                      <div>
                        <p className="text-xs text-blue-200 mb-1">
                          Phone
                        </p>

                        <p className="text-sm font-semibold">
                          +254 797 655 727
                        </p>
                      </div>
                    </a>

                    <a
                      href="https://www.nexinditechcompany.online/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 rounded-2xl bg-white/10 border border-white/10 p-4 hover:bg-white/15 transition"
                    >
                      <Globe size={18} className="text-blue-200" />

                      <div>
                        <p className="text-xs text-blue-200 mb-1">
                          Website
                        </p>

                        <p className="text-sm font-semibold">
                          nexinditechcompany.online
                        </p>
                      </div>
                    </a>
                  </div>
                </div>
              </section>
            </div>

            <p className="text-xs text-slate-400 leading-relaxed mt-8">
              This Privacy Policy is intended to provide general information
              about Nexindi Tech's privacy practices. Specific products or
              services may provide additional privacy notices where necessary.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;